import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, PauseCircle, Image, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const MilestoneApprovalModal = ({ milestone, open, onClose, onSuccess }) => {
  const [feedback, setFeedback] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleAction = async (action) => {
    if ((action === 'reject' || action === 'hold') && !feedback.trim()) {
      toast.error('Please provide feedback when rejecting or putting on hold');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/milestones/${milestone.milestone_id}/approve`, {
        action,
        feedback
      });

      const messages = {
        approve: 'Milestone approved and sent to customer for final approval',
        reject: 'Milestone rejected and sent back to engineer',
        hold: 'Milestone put on hold'
      };

      toast.success(messages[action]);
      setFeedback('');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} milestone`);
    } finally {
      setProcessing(false);
    }
  };

  if (!milestone) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900 flex items-center">
            Review Milestone Completion
            <Badge className="ml-3 bg-blue-600 text-white">Pending Review</Badge>
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Engineer has marked this milestone as complete. Review and take action.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Milestone Info */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-stone-900">{milestone.stage_name}</span>
            </div>
            {milestone.description && (
              <p className="text-sm text-stone-600">{milestone.description}</p>
            )}
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center text-stone-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Expected: {new Date(milestone.expected_end).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}</span>
              </div>
              {milestone.actual_end && (
                <div className="flex items-center text-green-600">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  <span>Completed: {new Date(milestone.actual_end).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}</span>
                </div>
              )}
            </div>
          </div>

          {/* Completion Photos */}
          {milestone.completion_photos?.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center text-sm font-medium">
                <Image className="h-4 w-4 mr-2 text-stone-500" />
                Completion Photos ({milestone.completion_photos.length})
              </Label>
              <div className="grid grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                {milestone.completion_photos.map((photo, idx) => (
                  <div key={idx} className="aspect-video bg-stone-200 rounded-lg overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.split('/').pop()}`}
                      alt={`Completion photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-stone-400 text-xs">Photo unavailable</div>';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback Field */}
          <div className="space-y-2">
            <Label htmlFor="feedback" className="text-sm font-medium text-stone-700">
              Feedback / Comments
              <span className="text-stone-400 font-normal ml-2">(Required for Reject/Hold)</span>
            </Label>
            <Textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Enter your feedback or reason..."
              rows={3}
              className="border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            <Button
              onClick={() => handleAction('reject')}
              disabled={processing}
              variant="outline"
              className="h-12 border-red-300 text-red-700 hover:bg-red-50 rounded-lg"
            >
              <XCircle className="mr-2 h-5 w-5" />
              Reject
            </Button>
            <Button
              onClick={() => handleAction('hold')}
              disabled={processing}
              variant="outline"
              className="h-12 border-orange-300 text-orange-700 hover:bg-orange-50 rounded-lg"
            >
              <PauseCircle className="mr-2 h-5 w-5" />
              Hold
            </Button>
            <Button
              onClick={() => handleAction('approve')}
              disabled={processing}
              className="h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              <CheckCircle2 className="mr-2 h-5 w-5" />
              Approve
            </Button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700 leading-relaxed">
              <strong>Approve:</strong> Sends to customer for final approval<br />
              <strong>Reject:</strong> Returns to engineer with feedback<br />
              <strong>Hold:</strong> Pauses milestone for further review
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MilestoneApprovalModal;
